import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

import FormButton from "../../components/form-components/FormButton";
import { HeaderPortal, useSetHeaderRecordName } from "../../components/ui-components/HeaderSlot.jsx";
import { primaryButtonCls, secondaryButtonCls } from "../../components/ui-components/buttonStyles.js";
import Skeleton from "../../components/ui-components/Skeleton";
import StatusPill from "../Budgets/components/StatusPill";
import Badge from "../../components/ui-components/Badge";
import Modal from "../../components/ui-components/Modal";
import { toast } from "../../components/ui-components/Toast";
import Input from "../../components/form-components/Input";
import { getUser, updateUser, suspendUser, reactivateUser, resendInvite, listRoles } from "./api";
import { computeRoleConflicts } from "./roleConflicts";
import RoleConflictWarning from "./RoleConflictWarning";
import UserScopeGrants from "./UserScopeGrants";
import ApprovalAuthorities from "./ApprovalAuthorities";
import { useUserScopeGrants, isScopedRole } from "./scopeApi";

// Maps the backend's SCOPE_REQUIRED (409, see backend_go
// internal/users/errors.go ErrScopeRequired) to a message that points the
// admin at the fix that's already on this page (the Data scope section
// below) — the client-side check below should normally catch this first, but
// the server is the source of truth (e.g. a grant removed in another tab).
function friendlyRoleSaveError(err) {
  if (err?.serverBody?.error?.code === "SCOPE_REQUIRED") {
    return "This role requires a data scope. Add one in the Data scope section below, then save again.";
  }
  return err?.serverBody?.error?.message || err?.message || "Update failed";
}

function SectionHeader({ title, helperText, statusChip }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="text-sm font-medium text-text">{title}</div>
        <div className="mt-0.5 text-xs text-text-2">{helperText}</div>
      </div>
      {statusChip && (
        <div className="shrink-0 flex items-center">
          <Badge status={statusChip} />
        </div>
      )}
    </div>
  );
}

const UserDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [function_, setFunction] = useState("");
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["managed-user", id],
    queryFn: () => getUser(id),
  });

  const { data: rolesData } = useQuery({
    queryKey: ["roles"],
    queryFn: listRoles,
    staleTime: 10 * 60 * 1000,
  });

  const scopeGrantsQ = useUserScopeGrants(id);

  const user = data?.user;
  const roles = useMemo(() => rolesData?.roles || [], [rolesData]);
  // Only platform + organisation roles are directly assignable — templates
  // seed new organisation roles via cloning (Roles & Access catalog) and
  // must never appear as togglable chips here (backend authz
  // IsAssignableSystemRole / `is_assignable`).
  const assignableRoles = useMemo(() => roles.filter((r) => r.is_assignable), [roles]);
  const scopeGrants = scopeGrantsQ.data || [];
  const rolesById = useMemo(() => {
    const m = {};
    for (const r of roles) m[r.code] = r;
    return m;
  }, [roles]);
  const roleConflicts = useMemo(
    () => computeRoleConflicts(selectedRoles, rolesById),
    [selectedRoles, rolesById]
  );

  // Scope-only roles (cost_owner/tech_cost_owner, or an organisation role
  // cloned from either) require at least one data scope grant on this user —
  // the backend rejects a zero-grant assignment with SCOPE_REQUIRED. Block
  // Save client-side too, so the admin sees the fix (the Data scope section
  // on this same page) before round-tripping. Checked against the role
  // objects (not just codes) so lineage carries through cloning.
  const hasScopeOnlyRoleSelected = selectedRoles.some((code) =>
    isScopedRole(rolesById[code])
  );
  const needsScopeGrant = hasScopeOnlyRoleSelected && scopeGrants.length === 0;

  useEffect(() => {
    if (user) {
      setFirstName(user.first_name || "");
      setLastName(user.last_name || "");
      setFunction(user.function || "");
      setSelectedRoles((user.roles || []).map((r) => r.code));
      setDirty(false);
    }
  }, [user]);

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ["managed-user", id] });
    queryClient.invalidateQueries({ queryKey: ["managed-users"] });
  };

  const updateMut = useMutation({
    mutationFn: (body) => updateUser(id, body),
    onSuccess: () => { invalidateAll(); setDirty(false); toast.success("User updated"); },
    onError: (err) => toast.error(friendlyRoleSaveError(err)),
  });

  const suspendMut = useMutation({
    mutationFn: () => suspendUser(id),
    onSuccess: () => { invalidateAll(); setConfirmAction(null); toast.success("User suspended"); },
    onError: (err) => { setConfirmAction(null); toast.error(err?.body?.error?.message || "Suspend failed"); },
  });

  const reactivateMut = useMutation({
    mutationFn: () => reactivateUser(id),
    onSuccess: () => { invalidateAll(); setConfirmAction(null); toast.success("User reactivated"); },
    onError: (err) => { setConfirmAction(null); toast.error(err?.body?.error?.message || "Reactivate failed"); },
  });

  const inviteMut = useMutation({
    mutationFn: () => resendInvite(id),
    onSuccess: (data) => {
      const token = data?.result?.invite_url;
      if (token) {
        const link = `${window.location.origin}/invite?token=${token}`;
        navigator.clipboard.writeText(link).catch(() => {});
        toast.success("Invite resent — link copied to clipboard");
      } else {
        toast.success("Invite resent");
      }
      invalidateAll();
    },
    onError: (err) => toast.error(err?.body?.error?.message || "Resend failed"),
  });

  const toggleRole = (code) => {
    setDirty(true);
    setSelectedRoles((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const handleSave = () => {
    if (needsScopeGrant) return;
    updateMut.mutate({ first_name: firstName, last_name: lastName, function: function_, roles: selectedRoles });
  };

  const isSaving = updateMut.isPending;
  const displayName = user?.first_name || user?.last_name
    ? `${user.first_name} ${user.last_name}`.trim()
    : user?.email || "User";

  useSetHeaderRecordName(user ? displayName : null);

  // -- Loading --
  if (isLoading) {
    return (
      <div className="p-8">
        <Skeleton height={400} />
      </div>
    );
  }

  // -- Error --
  if (error || !user) {
    return (
      <div className="h-full flex flex-col bg-background-grey">
        <div className="sticky top-0 z-40 bg-card border-b border-card-bd flex items-center h-[64px] px-4">
          <FontAwesomeIcon
            icon={icon({ name: "angle-left", family: "sharp", style: "solid" })}
            className="mr-4 text-sm cursor-pointer"
            onClick={() => navigate("/settings/users")}
          />
          <span className="text-lg text-text font-semibold font-['Plus_Jakarta_Sans',sans-serif]">
            User details
          </span>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="text-danger mb-2">User not found or error loading</div>
            <FormButton
              title="Back to list"
              style="secondary"
              onClick={() => navigate("/settings/users")}
            />
          </div>
        </div>
      </div>
    );
  }

  const statusKey = user.status || "invited";
  const keycloakChip = user.keycloak_linked ? "compliant" : "incomplete";

  return (
    <div className="h-full flex flex-col bg-background-grey relative">
      {/* Actions live in the global top bar (next to the tab switcher), styled like the
          primary/secondary buttons on /approvals/:id. */}
      <HeaderPortal>
        <div className="flex items-center gap-2.5">
          <button type="button" className={secondaryButtonCls} onClick={() => navigate("/settings/users")}>
            Cancel
          </button>
          <button
            type="button"
            className={primaryButtonCls}
            onClick={handleSave}
            disabled={!dirty || isSaving || needsScopeGrant}
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </HeaderPortal>

      {/* Title + status live in the header's row-1 title slot. */}
      <HeaderPortal slot="title">
        <div className="flex items-center gap-3 min-w-0">
          <div className="text-[20px] leading-[1.4] font-extrabold tracking-[-0.015em] text-text truncate font-['Plus_Jakarta_Sans',sans-serif]">
            {displayName}
          </div>
          <StatusPill status={statusKey} />
        </div>
      </HeaderPortal>

      {/* Scrollable content */}
      <div className="flex-1 overflow-auto p-6">
        {/* Suspended banner */}
        {user.status === "suspended" && (
          <div className="mb-4 px-4 py-3 bg-surface-2 border border-border rounded-lg text-sm text-text-2">
            This user is suspended and cannot access the system.
          </div>
        )}

        <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_360px] flex-1 min-w-0">
          {/* Left column: Profile + Roles */}
          <div className="space-y-6">
            {/* Profile card */}
            <div className="bg-card border border-card-bd rounded-card p-4 min-w-0 flex flex-col">
              <div className="border-b border-border pb-3 mb-4">
                <h3 className="text-sm font-semibold text-text">Profile</h3>
                <p className="text-xs text-text-2 mt-0.5">Basic user information.</p>
              </div>

              <div className="space-y-4">
                <section className="rounded-lg border border-border bg-surface-2/50 p-4">
                  <SectionHeader title="Email" helperText="Read-only. Set during invite." />
                  <div className="mt-3">
                    <Input
                      type="email"
                      value={user.email}
                      readOnly
                      noMarginBottom
                      inputClassName="bg-surface-2 text-text-2"
                    />
                  </div>
                </section>

                <section className="rounded-lg border border-border bg-surface-2/50 p-4">
                  <SectionHeader title="Name" helperText="Editable by admin." />
                  <div className="mt-3 flex gap-4">
                    <div className="flex-1">
                      <Input
                        title="First name"
                        name="firstName"
                        type="text"
                        value={firstName}
                        onChange={(e) => { setFirstName(e.target.value); setDirty(true); }}
                        noMarginBottom
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        title="Last name"
                        name="lastName"
                        type="text"
                        value={lastName}
                        onChange={(e) => { setLastName(e.target.value); setDirty(true); }}
                        noMarginBottom
                      />
                    </div>
                  </div>
                </section>

                <section className="rounded-lg border border-border bg-surface-2/50 p-4">
                  <SectionHeader title="Function" helperText="Job title or role in the organisation." />
                  <div className="mt-3">
                    <Input
                      title="Function"
                      name="function"
                      type="text"
                      value={function_}
                      onChange={(e) => { setFunction(e.target.value); setDirty(true); }}
                      placeholder="e.g. Finance Manager"
                      noMarginBottom
                    />
                  </div>
                </section>

                <div className="border-t border-border" aria-hidden />

                <section className="rounded-lg border border-border bg-surface-2/50 p-4">
                  <SectionHeader
                    title="Roles"
                    helperText="Determines what the user can access."
                    statusChip={selectedRoles.length > 0 ? `${selectedRoles.length} assigned` : "no roles"}
                  />
                  <div className="mt-3 flex flex-wrap gap-2">
                    {assignableRoles.map((role) => (
                      <button
                        key={role.code}
                        type="button"
                        onClick={() => toggleRole(role.code)}
                        className={`px-3 py-1.5 rounded text-sm border border-border transition-colors cursor-pointer ${
                          selectedRoles.includes(role.code)
                            ? "bg-primary text-white border-transparent"
                            : "bg-card text-text-3 border-border hover:border-border-strong hover:bg-surface-2"
                        }`}
                      >
                        {role.name}
                      </button>
                    ))}
                  </div>
                  <RoleConflictWarning conflicts={roleConflicts} />
                  {needsScopeGrant && (
                    <p className="mt-2 text-xs text-danger">
                      Assign a data scope before giving a scope-only role — add one in the Data
                      scope section below.
                    </p>
                  )}
                </section>
              </div>
            </div>

            {/* Data scope grants */}
            <UserScopeGrants userId={id} />

            {/* Approval authority grants */}
            <ApprovalAuthorities userId={id} />
          </div>

          {/* Right column: Status & Actions */}
          <div className="space-y-6">
            {/* Identity card */}
            <div className="bg-card border border-card-bd rounded-card p-4 min-w-0 flex flex-col">
              <div className="border-b border-border pb-3 mb-4">
                <h3 className="text-sm font-semibold text-text">Identity</h3>
                <p className="text-xs text-text-2 mt-0.5">Keycloak link and account status.</p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-2">Status</span>
                  <StatusPill status={statusKey} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-2">Keycloak</span>
                  <Badge status={keycloakChip} />
                </div>
                {user.last_login_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-text-2">Last login</span>
                    <span className="text-sm text-text">
                      {new Date(user.last_login_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-2">Created</span>
                  <span className="text-sm text-text">
                    {new Date(user.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions card */}
            <div className="bg-card border border-card-bd rounded-card p-4 min-w-0 flex flex-col">
              <div className="border-b border-border pb-3 mb-4">
                <h3 className="text-sm font-semibold text-text">Actions</h3>
                <p className="text-xs text-text-2 mt-0.5">Manage user access and invitations.</p>
              </div>

              <div className="space-y-2">
                {user.status !== "suspended" && (
                  <button
                    onClick={() => setConfirmAction("suspend")}
                    disabled={user.is_last_active_admin}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-text hover:bg-surface-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                    title={user.is_last_active_admin ? "Cannot suspend the last admin" : ""}
                  >
                    <FontAwesomeIcon
                      icon={icon({ name: "ban", style: "light" })}
                      className="w-4 h-4 text-text-2"
                    />
                    Suspend user
                  </button>
                )}
                {user.status === "suspended" && (
                  <button
                    onClick={() => setConfirmAction("reactivate")}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-text hover:bg-surface-2 transition-colors cursor-pointer"
                  >
                    <FontAwesomeIcon
                      icon={icon({ name: "rotate-right", style: "light" })}
                      className="w-4 h-4 text-text-2"
                    />
                    Reactivate user
                  </button>
                )}
                {(user.status === "invited" || !user.keycloak_linked) && (
                  <button
                    onClick={() => inviteMut.mutate()}
                    disabled={inviteMut.isPending}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-text hover:bg-surface-2 transition-colors cursor-pointer disabled:opacity-40"
                  >
                    <FontAwesomeIcon
                      icon={icon({ name: "envelope", style: "light" })}
                      className="w-4 h-4 text-text-2"
                    />
                    {inviteMut.isPending ? "Sending..." : "Resend invite"}
                  </button>
                )}
              </div>

              {user.is_last_active_admin && (
                <p className="mt-3 text-xs text-text-2">
                  This is the only active admin. Assign admin role to another user before suspending.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirm modals */}
      <Modal
        show={confirmAction === "suspend"}
        close={() => setConfirmAction(null)}
        title="Suspend user?"
        description={`${displayName} will lose access immediately.`}
        confirmButton="Suspend"
        cancelButton="Cancel"
        alertIcon
        confirmAction={() => suspendMut.mutate()}
        icon={icon({ name: "ban", style: "solid" })}
      />
      <Modal
        show={confirmAction === "reactivate"}
        close={() => setConfirmAction(null)}
        title="Reactivate user?"
        description={`${displayName} will regain access.`}
        confirmButton="Reactivate"
        cancelButton="Cancel"
        confirmAction={() => reactivateMut.mutate()}
        icon={icon({ name: "rotate-right", style: "solid" })}
      />
    </div>
  );
};

export default UserDetailPage;
